import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { LogIn, AlertTriangle, Info } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [passwort, setPasswort] = useState("");
  const [busy, setBusy] = useState(false);
  // Inline-Error: prominent über dem Form, sichtbar auch ohne <Toaster>.
  // (Toaster lebt im AppShell, der nicht auf der Login-Seite gerendert wird.)
  const [error, setError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);

  // Hinweis "Sitzung abgelaufen" anzeigen, wenn Interceptor uns hierher geleitet hat
  useEffect(() => {
    if (searchParams.get("session") === "expired") {
      setSessionExpired(true);
      try { window.sessionStorage.removeItem("session_expired"); } catch { /* ignore */ }
    }
  }, [searchParams]);

  if (user) {
    return <Navigate to={location.state?.from || "/"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSessionExpired(false);
    try {
      await login(username, passwort);
      // Respektiere `next`-Query (vom Interceptor gesetzt) — sonst location.state.from oder /
      const next = searchParams.get("next");
      const target = next || location.state?.from || "/";
      navigate(target, { replace: true });
    } catch (ex) {
      // Netzwerk-Fehler haben oft kein response-Objekt
      const detail = ex?.response?.data?.detail;
      const status = ex?.response?.status;
      let msg;
      if (status === 401) {
        msg = "Benutzername oder Passwort ist falsch. Bitte erneut versuchen.";
      } else if (status === 403) {
        msg = "Account ist deaktiviert. Bitte an Admin wenden.";
      } else if (!ex?.response) {
        msg = "Server nicht erreichbar. Bitte Verbindung prüfen.";
      } else {
        msg = detail || `Login fehlgeschlagen (HTTP ${status ?? "?"}).`;
      }
      setError(msg);
      // eslint-disable-next-line no-console
      console.error("[Login] Fehler beim Login:", { status, detail, ex });
    } finally {
      setBusy(false);
    }
  };

  // iOS-Safari-Detection für Cookie-Hinweis
  const isIOSSafari =
    typeof navigator !== "undefined" &&
    /iP(hone|ad|od)/.test(navigator.userAgent) &&
    /Safari/.test(navigator.userAgent) &&
    !/CriOS|FxiOS/.test(navigator.userAgent);

  return (
    <div data-testid="login-page" className="min-h-screen bg-ledger flex items-center justify-center px-4">
      <div className="w-[min(440px,95vw)]">
        <div className="text-center mb-8">
          <div className="font-display font-bold text-[28px] tracking-[-0.02em] text-gold leading-tight" style={{ color: "#C4922A" }}>
            Newton Campus Garching
          </div>
          <div className="text-xs uppercase tracking-widish text-navy/55 font-sans mt-2">
            Investoren-Akquise · Adldinger Unternehmensgruppe
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-navy/15 px-7 py-7 space-y-5">
          <h1 className="font-display font-bold text-navy text-2xl tracking-tight">Anmeldung</h1>

          {sessionExpired && !error && (
            <div
              data-testid="session-expired-banner"
              role="status"
              className="border-l-2 border-gold bg-gold/8 px-3 py-2.5 flex items-start gap-2"
            >
              <Info size={15} className="text-gold shrink-0 mt-0.5" strokeWidth={1.8} />
              <div className="text-xs font-sans text-navy/85 leading-snug">
                Deine Sitzung ist abgelaufen. Bitte erneut anmelden.
              </div>
            </div>
          )}

          {error && (
            <div
              data-testid="login-error"
              role="alert"
              className="border-l-2 border-tot bg-tot/5 px-3 py-2.5 flex items-start gap-2"
            >
              <AlertTriangle size={15} className="text-tot shrink-0 mt-0.5" strokeWidth={1.8} />
              <div className="text-xs font-sans text-navy/85 leading-snug">{error}</div>
            </div>
          )}

          <div>
            <label className="pb-label">Benutzername</label>
            <input
              data-testid="login-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              placeholder="heinrichs"
              className="pb-input--full"
            />
            <div className="text-[11px] text-navy/50 font-sans mt-1.5">
              Dein Nachname in Kleinbuchstaben
            </div>
          </div>
          <div>
            <label className="pb-label">Passwort</label>
            <input
              data-testid="login-passwort"
              type="password"
              autoComplete="current-password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              required
              className="pb-input--full"
            />
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="pb-btn-primary w-full justify-center text-sm uppercase tracking-widish font-semibold py-3"
          >
            <LogIn size={14} /> {busy ? "Anmeldung läuft …" : "Anmelden"}
          </button>
        </form>

        {isIOSSafari && (
          <div
            data-testid="ios-safari-hint"
            className="mt-4 bg-white border border-navy/15 px-4 py-3 text-[11px] font-sans text-navy/75 leading-relaxed"
          >
            <div className="font-medium text-navy mb-1 flex items-center gap-1.5">
              <Info size={12} className="text-gold"/> Hinweis für iPhone / iPad
            </div>
            Falls die Anmeldung nicht funktioniert: Safari → <span className="font-medium">Einstellungen → Datenschutz</span> → „Cross-Site-Tracking verhindern" <span className="font-medium">deaktivieren</span> oder Webseiten-Cookies für diese Domain zulassen.
          </div>
        )}

        <p className="text-[11px] text-navy/45 font-sans text-center mt-6">
          Interner Zugang nur für autorisierte Nutzer der Adldinger Unternehmensgruppe.
        </p>
      </div>
    </div>
  );
}
